<template>
  <TopbarWrapper :logo-url="logoUrl">
    <div class="signing-page-top-bar-content">
      <template v-if="isXSmallScreenAndDown">
        <FriedIconButton
          v-if="isInviteEnabled"
          :disabled="isPreview"
          data-external="signing-page-topbar-invite-button"
          @click="$emit('invite')"
        >
          <FriedIcon icon="user-add" />
        </FriedIconButton>
        <ChatButton
          v-if="isChatEnabled"
          :disabled="isPreview"
          :new-comments-count="newCommentsCount"
          @click="$emit('chat')"
        />
        <FriedIconButton
          data-external="signing-page-top-bar-menu-button"
          @click="$emit('toggle-menu', true)"
        >
          <FriedIcon icon="hamburger-menu" />
        </FriedIconButton>
      </template>
      <template v-else>
        <FriedButtonSplit
          v-if="isSigner"
          data-external="signing-page-topbar-invite-button"
          button-type="secondary"
          :disabled="isPreview"
          @click="$emit('invite')"
        >
          <template #leftIcon>
            <FriedIcon icon="user-add" />
          </template>
          {{ $t('invite') }}
          <template #dropdownItems>
            <FriedDropdownItem
              data-external="signing-page-topbar-change-signer-button"
              @click="$emit('invite', true)"
            >
              <template #leftIcon>
                <FriedIcon icon="forward-circle" />
              </template>
              {{ $t('change_signer') }}
            </FriedDropdownItem>
          </template>
        </FriedButtonSplit>
        <FriedButton
          v-else-if="isInviteEnabled"
          data-external="signing-page-topbar-invite-button"
          button-type="secondary"
          :disabled="isPreview"
          @click="$emit('invite')"
        >
          <template #leftIcon>
            <FriedIcon icon="user-add" />
          </template>
          {{ $t('invite') }}
        </FriedButton>
        <FriedButton
          class="menu-button"
          :disabled="isPreview"
          button-type="tertiary"
          data-external="signing-page-top-bar-menu-button"
          @click="$emit('toggle-menu', true)"
        >
          <template #leftIcon>
            <FriedIcon icon="hamburger-menu" />
          </template>
          {{ $t('menu') }}
        </FriedButton>
      </template>
      <SideMenu
        :show="showMenu"
        :sender="sender"
        :recipient="recipient"
        :recipients="recipients"
        :new-comments-count="newCommentsCount"
        :is-preview="isPreview"
        :is-call-enabled="isCallEnabled"
        :is-download-enabled="isDownloadEnabled"
        :is-print-enabled="isPrintEnabled"
        :is-chat-enabled="isChatEnabled"
        :is-invite-enabled="isInviteEnabled"
        :is-reject-enabled="isRejectEnabled"
        :is-signer="isSigner"
        :is-signing-order="isSigningOrder"
        :show-contextual-comments="showContextualComments"
        :prevent-comments-modification="preventCommentsModification"
        @invite="$emit('invite')"
        @action="$emit('action', $event)"
        @call="$emit('call')"
        @toggle="$emit('toggle-menu', $event)"
        @close="$emit('toggle-menu', false)"
      />
    </div>
  </TopbarWrapper>
</template>

<script lang="ts">
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useI18n } from 'vue-i18n';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';
import TopbarWrapper from '../../../components/TopbarWrapper.vue';
import SideMenu from '../../sidebar/components/SideMenu.vue';
import ChatButton from './ChatButton.vue';

export default defineComponent({
  components: {
    TopbarWrapper,
    ChatButton,
    SideMenu,
  },
  props: {
    logoUrl: {
      type: String,
      default: '',
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      default: () => [],
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      default: () => ({}),
      required: true,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      default: () => ({}),
      required: true,
    },
    newCommentsCount: {
      type: Number,
      default: 0,
    },
    showContextualComments: Boolean,
    isDownloadEnabled: Boolean,
    isPrintEnabled: Boolean,
    isCallEnabled: Boolean,
    isPreview: Boolean,
    isChatEnabled: Boolean,
    isRejectEnabled: Boolean,
    preventCommentsModification: Boolean,
    isSigningOrder: Boolean,
    showMenu: Boolean,
  },
  emits: ['toggle-menu', 'invite', 'action', 'chat', 'call'],
  setup(props) {
    const { isXSmallScreenAndDown } = useBreakpoints();
    const { t } = useI18n();

    const isInviteEnabled = computed(
      () => !RecipientsHelper.isInternalApprover(props.recipient?.role)
    );
    const isSigner = computed(() => props.recipient?.role === RecipientRole.Signer);

    return {
      isXSmallScreenAndDown,
      isInviteEnabled,
      Placement,
      isSigner,
      t,
    };
  },
});
</script>

<style lang="scss" scoped>
.signing-page-top-bar-content {
  display: flex;
  justify-content: right;
  align-items: center;
  width: 100%;
  gap: var(--spacing-50);

  @media screen and (max-width: $xs) {
    gap: var(--spacing-50);
  }

  .menu-button:not(.color) {
    background-color: white;
  }
}
</style>
